// Daily bonus (spec/dashboard.md section 8.2): cards over LiveOps.DailyBonus with the type chip (the reset rule in its
// tooltip), the condition, the bonus reward and the days grid — rows of seven over `rewards`, every change one
// whole-list edit, an empty day a warning. The days sort by drag (their handle; the keyboard sensor too) across the rows of
// seven, a drop re-indexing `rewards` as one whole-value edit like the menu's Move items; Duplicate day inserts a copy after.
import { useState, type ReactNode } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Json, Parameter } from '../../api/types';
import { CardList, type CardContext } from '../../content/cards/CardList';
import { Chip as FieldChip, paramOf } from '../../content/cards/Chips';
import { ConditionSummary } from '../../content/conditions/ConditionBuilder';
import { useContent } from '../../content/ContentContext';
import { itemAmountText } from '../../content/format';
import { FieldWidget } from '../../content/widgets/FieldWidget';
import { Button, IconButton } from '../../ui/Button';
import { AlertTriangle, Filter, Gift, GripVertical, MoreVertical, Plus } from '../../ui/icons';
import { Menu, Popover } from '../../ui/Popover';
import { BodyField, CommonTail, NameText, paramsOf } from './cards';
import './liveops.css';

export const DAILY_BONUS = 'LiveOps.DailyBonus';

/** The reset rules of spec/liveops-engine.md section 10, as the type chip's tooltip states them. */
export const DAILY_BONUS_RULES: Record<number, string> = {
  0: 'CollectAllToReset: resets once everything is collected and a new claim is possible; skipped days are ignored',
  1: 'SkipToReset: a skipped day, or a completed cycle, resets the progress',
  2: 'CalendarReset: resets when the game-time month changes, never on completion',
  3: 'SkipToResetCurrentWeek: a skipped day drops back to the start of the current block of seven; a completed cycle resets',
};

export function DailyBonusPage() {
  return (
    <CardList
      id="daily-bonus"
      title="Daily bonus"
      template={DAILY_BONUS}
      includeHeirs={false}
      header={(ctx) => <BonusHeader ctx={ctx} />}
      body={(ctx) => <BonusBody ctx={ctx} />}
      filterFields={[{ key: 'data.type', label: 'Type', type: 'enum', group: 'Daily bonus', options: [0, 1, 2, 3].map((v) => ({ value: String(v), label: DAILY_BONUS_RULES[v]!.split(':')[0]! })) }]}
      emptyText="No daily bonus yet. A daily bonus hands out one reward per game-time day along its days grid; the first passing bonus in content order is the active one."
      emptyIcon={<Gift aria-hidden="true" />}
    />
  );
}

function BonusHeader({ ctx }: { ctx: CardContext }) {
  const params = paramsOf(DAILY_BONUS);
  const d = (ctx.doc.data ?? {}) as Record<string, Json>;
  const type = paramOf(params, 'type');
  const days = Array.isArray(d.rewards) ? d.rewards.length : 0;
  return (
    <div className="card-head-lines">
      <div className="lo-head-line">
        <NameText doc={ctx.doc} />
        <span className="lo-meta">
          {days} day{days === 1 ? '' : 's'}
        </span>
        <span className="lo-cond">
          <Filter aria-hidden="true" />
          <ConditionSummary rootId={typeof d.condition === 'string' ? d.condition : null} />
        </span>
      </div>
      <div className="card-chips">
        {type && (
          <span title={DAILY_BONUS_RULES[Number(d.type ?? 0)] ?? ''} data-testid="daily-bonus-type">
            <FieldChip doc={ctx.doc} param={type} disabled={!ctx.canWrite} onCommit={(v) => ctx.edit('data.type', v)} changed={ctx.draft?.changes.some((c) => c.path === 'type')} />
          </span>
        )}
      </div>
    </div>
  );
}

function BonusBody({ ctx }: { ctx: CardContext }) {
  const params = paramsOf(DAILY_BONUS);
  const d = (ctx.doc.data ?? {}) as Record<string, Json>;
  const bonusItems = d.bonusReward && typeof d.bonusReward === 'object' && !Array.isArray(d.bonusReward) ? ((d.bonusReward as Record<string, Json>).items as Json[] | undefined) : undefined;
  const rewardsParam = paramOf(params, 'rewards');
  const days = Array.isArray(d.rewards) ? d.rewards.length : 0;
  return (
    <div className="card-fields lo-body solo">
      <div className="card-fields-main">
        <Group title="Daily bonus">
          <BodyField ctx={ctx} params={params} name="name" />
        </Group>
        <Group title="Condition" single>
          <BodyField ctx={ctx} params={params} name="condition" />
        </Group>
        <Group title="Days">
          <div className="lo-days-head">
            <p className="lo-note" data-testid="daily-bonus-preview">
              {days === 0 ? 'No days yet.' : `${days} day${days === 1 ? '' : 's'}, then ${Array.isArray(bonusItems) && bonusItems.length > 0 ? 'the bonus reward' : 'the cycle resets'}.`}
            </p>
          </div>
          {rewardsParam && <DaysGrid ctx={ctx} param={rewardsParam} />}
        </Group>
        <Group title="Bonus reward" single>
          <BodyField ctx={ctx} params={params} name="bonusReward" help="Claimable once after the regular days; when empty the cycle ends at the last day" />
        </Group>
        <Group title="Notes">
          <CommonTail ctx={ctx} />
        </Group>
      </div>
    </div>
  );
}

/** A titled group of the expanded body (the card pages' anatomy); `single` hides the lone field's label visually. */
function Group({ title, single, children }: { title: string; single?: boolean; children: ReactNode }) {
  return (
    <section className={`lo-group ${single ? 'single' : ''}`} aria-label={title}>
      <h4 className="lo-group-title">{title}</h4>
      {children}
    </section>
  );
}

function rewardOf(v: Json | undefined): Json[] {
  return v && typeof v === 'object' && !Array.isArray(v) && Array.isArray((v as Record<string, Json>).items) ? ((v as Record<string, Json>).items as Json[]) : [];
}

/** The days grid: rows of seven over `rewards`; every change is one whole-list edit (undo restores the grid). */
export function DaysGrid({ ctx, param }: { ctx: CardContext; param: Parameter }) {
  const catalog = useContent();
  const rewards = Array.isArray((ctx.doc.data as Record<string, Json>).rewards) ? ((ctx.doc.data as Record<string, Json>).rewards as Json[]) : [];
  const [editing, setEditing] = useState<{ index: number; anchor: HTMLElement } | null>(null);
  const [menu, setMenu] = useState<{ index: number; anchor: HTMLElement } | null>(null);
  const set = (next: Json[]) => ctx.edit('data.rewards', next);
  /** Moves day `from` to position `to` (the menu's neighbour moves and a drop alike): one whole-list edit. */
  const moveTo = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= rewards.length || to >= rewards.length) return;
    set(arrayMove(rewards, from, to));
  };
  const duplicate = (i: number) => set([...rewards.slice(0, i + 1), JSON.parse(JSON.stringify(rewards[i] ?? null)) as Json, ...rewards.slice(i + 1)]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const dayIds = rewards.map((_, i) => dayId(i));
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id || !ctx.canWrite) return;
    moveTo(dayIds.indexOf(String(active.id)), dayIds.indexOf(String(over.id)));
  };
  const weeks: number[][] = [];
  for (let i = 0; i < rewards.length; i += 7) weeks.push(rewards.slice(i, i + 7).map((_, k) => i + k));
  if (weeks.length === 0 || weeks[weeks.length - 1]!.length === 7) weeks.push([]);
  const of = param.of ?? { ...param, type: 'object' as const, template: 'SmartObjects.Reward' };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
    <SortableContext items={dayIds} strategy={rectSortingStrategy}>
    <div className="days-grid lo-days" role="table" aria-label="Days">
      <div className="days-row days-head" role="row">
        <span role="columnheader">Week</span>
        {Array.from({ length: 7 }, (_, k) => (
          <span key={k} role="columnheader">
            Day {k + 1}
          </span>
        ))}
      </div>
      {weeks.map((indices, w) => (
        <div key={w} className="days-row" role="row">
          <span role="rowheader">Week {w + 1}</span>
          {Array.from({ length: 7 }, (_, k) => {
            const index = indices[k];
            if (index === undefined) {
              return k === indices.length && ctx.canWrite ? (
                <button key={k} type="button" role="cell" className="day-add" onClick={() => set([...rewards, { items: [] } as unknown as Json])} aria-label={`Add day ${rewards.length + 1}`}>
                  <Plus aria-hidden="true" />
                  <span aria-hidden="true">Day {rewards.length + 1}</span>
                </button>
              ) : (
                <span key={k} role="cell" className="day-cell day-blank" />
              );
            }
            const items = rewardOf(rewards[index]);
            return (
              <DayCell key={k} index={index} canWrite={ctx.canWrite} empty={items.length === 0}>
                <button type="button" className="day-body" onClick={(e) => ctx.canWrite && setEditing({ index, anchor: e.currentTarget })} aria-label={`Day ${index + 1}`} disabled={!ctx.canWrite}>
                  <span className="lo-day-num">Day {index + 1}</span>
                  {items.length === 0 ? (
                    <span className="lo-day-warn">
                      <AlertTriangle aria-hidden="true" /> empty day
                    </span>
                  ) : (
                    items.map((it, n) => (
                      <span key={n} className="lo-day-line">
                        {itemAmountText(it, catalog.format)}
                      </span>
                    ))
                  )}
                </button>
                {ctx.canWrite && (
                  <IconButton label={`Day ${index + 1} actions`} onClick={(e) => setMenu({ index, anchor: e.currentTarget })}>
                    <MoreVertical aria-hidden="true" />
                  </IconButton>
                )}
              </DayCell>
            );
          })}
        </div>
      ))}
      {editing && (
        <Popover anchor={editing.anchor} open onClose={() => setEditing(null)} label={`Day ${editing.index + 1}`} className="day-popover">
          <FieldWidget
            param={{ ...of, name: `rewards[${editing.index}]`, displayName: `Day ${editing.index + 1}` }}
            value={rewards[editing.index]}
            owner={ctx.doc.id}
            onCommit={(v) => set(rewards.map((r, i) => (i === editing.index ? v : r)))}
          />
          <div className="lo-day-popover-foot">
            <Button size="s" onClick={() => setEditing(null)}>
              Done
            </Button>
          </div>
        </Popover>
      )}
      <Menu
        anchor={menu?.anchor ?? null}
        open={menu !== null}
        onClose={() => setMenu(null)}
        label="Day actions"
        items={
          menu
            ? [
                { key: 'left', label: 'Move left', onSelect: () => moveTo(menu.index, menu.index - 1), disabled: menu.index === 0 },
                { key: 'right', label: 'Move right', onSelect: () => moveTo(menu.index, menu.index + 1), disabled: menu.index >= rewards.length - 1 },
                { key: 'duplicate', label: 'Duplicate day', onSelect: () => duplicate(menu.index) },
                { key: 'delete', label: 'Delete day', onSelect: () => set(rewards.filter((_, i) => i !== menu.index)), danger: true },
              ]
            : []
        }
      />
    </div>
    </SortableContext>
    </DndContext>
  );
}

const dayId = (index: number) => `day-${index}`;

/** One day of the grid, sortable by its handle across the rows of seven. */
function DayCell({ index, canWrite, empty, children }: { index: number; canWrite: boolean; empty: boolean; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: dayId(index), disabled: !canWrite });
  return (
    <div ref={setNodeRef} role="cell" className={`day-cell${empty ? ' day-empty' : ''}${isDragging ? ' dragging' : ''}`} data-testid={`day-${index + 1}`} style={{ transform: CSS.Translate.toString(transform), transition }}>
      {children}
      {canWrite && (
        <button type="button" className="drag-handle lo-day-handle" aria-label={`Move day ${index + 1}`} title="Drag to reorder the days" {...attributes} {...listeners}>
          <GripVertical aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
